import { useStore } from '@nanostores/react';
import { type HTMLMotionProps } from 'framer-motion';
import { Suspense, useState } from 'react';
import { toast } from 'react-toastify';
import type { ScrollToBottom } from 'use-stick-to-bottom';
import { PinnedMessage, usePinnedMessageToggleState } from '~/components/chat/PinnedMessage';
import {
  PreviewRenderer,
  ShellRenderer,
  SupabaseFunctionRenderer,
  SupabaseRenderer,
  TerminalRenderer,
} from '~/components/chat/problems/renderers';
import { Counter } from '~/components/ui/Counter';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { errorHandler } from '~/lib/error-handling';
import { chatStore } from '~/lib/stores/chat';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import { type PotentialProblem } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { plural } from '~/utils/plural';

export interface PotentialProblemsProps extends HTMLMotionProps<'div'> {
  resolved: boolean;
  problems: PotentialProblem[];
  isLast: boolean;
  consecutiveProblemCount: number;
  messageBoxHeight: number;
  scrollToBottom: ScrollToBottom;
  onStickyHeight: (height: number) => void;
  customResolve?: () => Promise<void>;
  customClear?: () => Promise<void>;
}

export function PotentialProblems({
  problems,
  resolved,
  messageBoxHeight,
  isLast,
  consecutiveProblemCount,
  onStickyHeight,
  scrollToBottom,
  customResolve,
  customClear,
}: PotentialProblemsProps) {
  const [resolving, setResolving] = useState(false);
  const [context, setContext] = useState('');

  const loggedIn = useStore(userStore.loggedIn);
  const isProcessing = useStore(workbenchStore.isProcessing);

  const pluralProblem = plural(problems.length, 'problem');

  const { setToggled, toggled } = usePinnedMessageToggleState({ scrollToBottom, isLast });

  const getTitle = () => {
    if (resolving) {
      return `Bolt is trying to resolve the ${pluralProblem}.`;
    }

    if (resolved) {
      return `Bolt has tried to resolve the ${pluralProblem}.`;
    }

    if (isLast) {
      return `Potential ${pluralProblem} detected.`;
    }

    return 'May already be resolved or irrelevant.';
  };

  const handleResolve = () => {
    if (!loggedIn) {
      return;
    }

    chatStore.promptMode.set('build');

    scrollToBottom?.();

    setResolving(true);

    setToggled(false);

    Promise.resolve()
      .then(() => {
        if (customResolve) {
          return customResolve();
        }

        return errorHandler.resolve(context);
      })
      .catch((error) => toast.error(error))
      .finally(() => {
        setResolving(false);
        setContext('');
      });
  };

  if (isLast && isProcessing && !resolved) {
    /**
     * If we are streaming and not fixing an error, hide the errors that occur in this period. (Previous errors are
     * already cleared when adding a new message).
     *
     * If they are no longer relevant, we will remove them before this component is shown again.
     *
     * Checking for `!resolved` is what we use to discern that is this not an error fix stream.
     */
    return false;
  }

  return (
    <PinnedMessage
      isLast={isLast}
      onStickyHeight={onStickyHeight}
      messageBoxHeight={messageBoxHeight}
      scrollToBottom={scrollToBottom}
      setToggled={setToggled}
      toggled={toggled}
      sticky={!resolved}
      title={
        <>
          <div
            className={classNames('text-lg', {
              'i-svg-spinners:90-ring-with-bg text-bolt-elements-loader-progress': resolving,
              'i-ph:check text-bolt-elements-icon-success': !resolving && resolved,
              'i-ph:warning': !resolving && !resolved,
              'text-bolt-elements-icon-error': isLast && !resolved,
            })}
          />
          {getTitle()}
        </>
      }
      toggle={({ onScreen }) => (
        <>
          <Counter
            overflow={10}
            onChange={() => {
              // if not on screen un-toggle to make it sticky again
              if (!onScreen) {
                setToggled(false);
              }
            }}
          >
            {problems.length}
          </Counter>
          {toggled ? 'Hide' : 'Show'} {pluralProblem}
        </>
      )}
      actions={
        isLast &&
        !resolved && (
          <div className="flex flex-col gap-2 justify-between">
            {!customResolve && (
              <textarea
                value={context}
                onChange={(event) => setContext(event.target.value)}
                disabled={resolving}
                className="text-sm p-2.5 w-full mb-1 rounded-md resize-none h-full placeholder-bolt-elements-textTertiary outline-none focus:ring-0 focus:outline-none bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 disabled:cursor-not-allowed"
                placeholder="Provide more info about this issue to help Bolt fix it."
              />
            )}
            <div className="text-bolt-elements-textSecondary text-sm mb-1">
              {consecutiveProblemCount >= 5
                ? `Bolt tried to fix ${plural(problems.length, 'this problem', 'these problems')} ${consecutiveProblemCount} times but failed. To prevent spending more tokens, you might want to try to fix it manually or rollback to a previous checkpoint instead.`
                : `Should we try to fix ${plural(problems.length, 'this problem', 'these problems')}?`}
            </div>
            <div className="flex gap-2.5">
              <LegacyButton variant="primary" size="sm" disabled={resolving} onClick={handleResolve}>
                Attempt fix
              </LegacyButton>

              <LegacyButton
                variant="secondary"
                size="sm"
                disabled={resolving}
                onClick={() => {
                  if (customClear) {
                    customClear();
                  } else {
                    errorHandler.clear();
                  }
                }}
              >
                Ignore
              </LegacyButton>
            </div>
          </div>
        )
      }
      messageClassName={classNames('before:border-bolt-elements-messages-error-borderColor', {
        'before:border-l-3': isLast && !resolved,
      })}
    >
      {problems.map((error, i) => (
        <Suspense key={i} fallback={null}>
          {error.type === 'preview' ? (
            <PreviewRenderer {...error} />
          ) : error.type === 'shell' ? (
            <ShellRenderer scrollToBottom={scrollToBottom} {...error} />
          ) : error.type === 'terminal' ? (
            <TerminalRenderer {...error} />
          ) : error.type === 'supabase-migration' ? (
            <SupabaseRenderer {...error} />
          ) : error.type === 'supabase-function' ? (
            <SupabaseFunctionRenderer {...error} />
          ) : (
            <div>Unknown error type</div>
          )}
        </Suspense>
      ))}
    </PinnedMessage>
  );
}
